import { ApexOptions } from 'apexcharts';

import { TEXT_CONTENT } from '../../../../../utils/constants';
import {
  DATE_FORMAT,
  WEEK_DAYS,
  isValidDate,
  toDisplayDate,
} from '../../../../../utils/date';
import { Shift } from '../../../contexts/ShiftProvider.types';
import { GraphData } from './PtArrivalsPtsCovered.types';

type TPtArrivalsPtsCoveredData = {
  dateHour: string;
  arrivals: number;
  physicianLoad: number;
  appLoad: number;
  physicianHourScheduled: number;
  appHourScheduled: number;
  patientsPerCorrectedHour: number;
  patientHandled: number;
};
type TShiftData = {
  shiftId: string;
  shiftStart: string;
  shiftEnd: string;
  shiftLength: number;
  providerType?: string;
};
export const PtsPatientArrivalKeyLabel = {
  arrivals: TEXT_CONTENT.PATIENT_ARRIVALS,
  physicianLoad: TEXT_CONTENT.PHYSICIAN_PATIENT_ARRIVALS,
  appLoad: TEXT_CONTENT.APP_PATIENT_ARRIVALS,
  physicianHourScheduled: TEXT_CONTENT.PHYSICIAN_HOURS_SCHEDULED,
  appHourScheduled: TEXT_CONTENT.APP_HOURS_SCHEDULED,
  patientsPerCorrectedHour: TEXT_CONTENT.PATIENTS_PER_CORRECTED_HOUR,
  patientHandled: TEXT_CONTENT.PATIENTS_COVERED_WITH_SHIFT_MAP,
};
const summaryTableRows = [
  'arrivals',
  'physicianHourScheduled',
  'appHourScheduled',
  'patientsPerCorrectedHour',
  'patientHandled',
];
interface TableRow {
  [key: string]: any;
}
export interface ITableDataFilterParams {
  selectedDates?: Array<string | number>;
  isRange?: boolean;
  trimCols?: boolean;
}
interface TgroupByDay {
  [key: string | number]: TPtArrivalsPtsCoveredData[];
}
export interface TPtArrivalsPtsCovered {
  data: Array<any>;
  shift: Shift[];
}

export interface AppPhysicianCount {
  app: 0;
  physician: 0;
}
export interface AppPhysicianCountCollection {
  [key: string]: AppPhysicianCount;
}
export class PtArrivalsPtsCoveredModel {
  data: TPtArrivalsPtsCoveredData[] = [];
  shiftData: TShiftData[] = [];

  constructor(res: TPtArrivalsPtsCovered) {
    const appPhyCount: AppPhysicianCountCollection = {};
    this.shiftData =
      res?.shift
        ?.filter(
          (each) => isValidDate(each?.startDate) && isValidDate(each?.endDate)
        )
        .map((each) => {
          let startDate = new Date(each.startDate);
          for (let i = 0; i < each.shiftLength; i++) {
            const currDt = startDate.toISOString();

            let _appPhyCount = appPhyCount?.[currDt] || {
              app: 0,
              physician: 0,
            };
            if (each.providerType?.toLowerCase() === 'app') {
              _appPhyCount.app += 1;
            }
            if (each.providerType?.toLowerCase() === 'physician') {
              _appPhyCount.physician += 1;
            }
            appPhyCount[currDt] = _appPhyCount;

            if (startDate.getDay() === 6 && startDate.getHours() === 23) {
              startDate.setDate(startDate.getDate() - 6);
              startDate.setHours(0);
            } else startDate.setHours(startDate.getHours() + 1);
          }
          return {
            shiftId: each?.id || '',
            shiftEnd: each?.endDate || '',
            shiftStart: each?.startDate || '',
            shiftLength: each?.shiftLength || 0,
            providerType: each?.providerType || '',
          };
        }) || [];
    this.data =
      res?.data
        ?.filter((each) => isValidDate(each?.date_hour))
        .map((each) => {
          let dateHour = new Date(each.date_hour);
          const currCount = appPhyCount[dateHour.toISOString()];
          const ptsPerCorrectedHr =
            (Number(each?.arrivals) || 0) /
            ((currCount?.physician || 0) + (currCount?.app || 0) / 2);
          return {
            dateHour: new Date(each?.date_hour).toISOString(),
            arrivals: Number(each?.arrivals) || 0,
            physicianLoad: Number(each?.physician_load) || 0,
            appLoad: Number(each?.app_load) || 0,
            // physicianHourScheduled: Number(each?.physician_hours_scheduled) || 0,
            // appHourScheduled: Number(each?.app_hours_scheduled) || 0,
            physicianHourScheduled: currCount?.physician || 0,
            appHourScheduled: currCount?.app || 0,
            patientsPerCorrectedHour: Number.isFinite(ptsPerCorrectedHr)
              ? ptsPerCorrectedHr
              : 0,
            // Number(each?.patients_per_corrected_hour) || 0,
            patientHandled: Number(each?.patient_handled) || 0,
          };
        }) || [];
    this.sortByDate();
  }
  sortByDate() {
    this.data = this?.data?.sort?.((a, b) => {
      let d1 = new Date(a.dateHour);
      d1.setHours(0, 0, 0, 0);
      let d2 = new Date(b.dateHour);
      d2.setHours(0, 0, 0, 0);
      return d1.valueOf() - d2.valueOf();
    });
    return this;
  }

  groupByDay() {
    return this.data?.reduce?.((acc: TgroupByDay, item) => {
      let newDt = new Date(item.dateHour);
      newDt.setHours(0, 0, 0, 0);
      const groupKey = newDt.toISOString();
      acc[groupKey] = acc[groupKey] || [];
      acc[groupKey].push(item);
      return acc;
    }, {});
  }
  getSelectedDayData(selectedDay?: string) {
    if (selectedDay) {
      const dayGroupedData = this.groupByDay();
      const selectedIndex = Object.keys(dayGroupedData).find((key) => {
        return new Date(key).getDay()?.toString() === selectedDay;
      });

      if (selectedIndex) {
        return dayGroupedData?.[selectedIndex] || [];
      }
      return [];
    }
    return this.data || [];
  }
  getSelectedDayShifts(selectedDay?: string) {
    if (selectedDay) {
      return this.shiftData.filter((d) => {
        const startDate = new Date(d.shiftStart);
        const shiftEndDate = new Date(d.shiftEnd);
        if (
          startDate.getDay()?.toString() === selectedDay ||
          shiftEndDate.getDay()?.toString() === selectedDay
        )
          return true;
        else return false;
      });
    }
    return this.shiftData || [];
  }
  getAllDayWiseSummary() {
    const dayGroupedData = this.groupByDay();
    const cols: number[] = [];
    const aggregatedColRows: any = {};
    Object.entries(dayGroupedData).forEach(([key, value]) => {
      const colKey = new Date(key).valueOf();
      cols.push(colKey);

      const aggregatedValues: { [index: string]: number } = value.reduce(
        (prev, curr, index, arr) => {
          prev.arrivals += curr.arrivals;
          prev.physicianHourScheduled += curr.physicianHourScheduled;
          prev.appHourScheduled += curr.appHourScheduled;
          prev.patientsPerCorrectedHour = 0;
          // prev.patientsPerCorrectedHour =
          //   prev.patientsPerCorrectedHour +
          //   curr.patientsPerCorrectedHour / arr.length;
          prev.patientHandled += curr.patientHandled;
          if (index === arr.length - 1) {
            const val =
              prev.arrivals /
              (prev.physicianHourScheduled + prev.appHourScheduled / 2);

            prev.patientsPerCorrectedHour = Number.isFinite(val) ? val : 0;
          }
          return prev;
        },
        {
          arrivals: 0,
          physicianHourScheduled: 0,
          appHourScheduled: 0,
          patientsPerCorrectedHour: 0,
          patientHandled: 0,
        }
      );

      aggregatedColRows[colKey] = aggregatedValues;
    });
    const rows = summaryTableRows.map((d) => {
      const weeCols: any = {};
      cols.forEach((cold) => {
        weeCols[cold] = aggregatedColRows?.[cold]?.[d] || 0;
      });
      const row: TableRow = {
        id: d,
        category:
          PtsPatientArrivalKeyLabel[
            d as keyof typeof PtsPatientArrivalKeyLabel
          ] || '',
        ...weeCols,
        // [colKey]:
        //   aggregatedValues[d as keyof typeof PtsPatientArrivalKeyLabel] || 0,
      };
      return row;
    });
    return { rows, cols };
  }
  getTableData(selectedDay?: string) {
    let cols: number[] = [];
    let rows: TableRow[] = [];
    let data = this.getSelectedDayData(selectedDay);
    data.forEach?.((each) => {
      let { dateHour } = each;
      let currDate = new Date(dateHour);
      cols.push(currDate.valueOf());

      summaryTableRows.forEach((d) => {
        let row: TableRow = {
          id: d,
          category:
            PtsPatientArrivalKeyLabel[
              d as keyof typeof PtsPatientArrivalKeyLabel
            ] || '',
          [`${currDate.valueOf()}`]: each[d as keyof TPtArrivalsPtsCoveredData],
        };

        const findIndex = rows.findIndex((newData) => {
          return String(newData.id) === String(d);
        });

        if (findIndex <= -1) {
          rows.push(row);
        } else {
          rows[findIndex] = { ...rows[findIndex], ...row };
        }
      });
    });

    return {
      rows: rows,
      cols,
    };
  }
  getAllDays() {
    let data: { id: number; name: string }[] = [];
    Object.keys(this.groupByDay())
      ?.sort()
      ?.forEach?.((item) => {
        let date: any = new Date(item);
        const dayOfWeek: number = date.getDay();
        const dayName: string = WEEK_DAYS[dayOfWeek];
        data.push({
          id: dayOfWeek,
          name: dayName,
        });
        return true;
      });
    return data;
  }
  getGraphData(selectedDay?: string) {
    let data = this.getSelectedDayData(selectedDay);
    let shiftData = this.getSelectedDayShifts(selectedDay);
    let seriesData: GraphData[] = [
      {
        type: 'column',
        name: PtsPatientArrivalKeyLabel.physicianLoad,
        data: [],
      },
      {
        type: 'column',
        name: PtsPatientArrivalKeyLabel.appLoad,
        data: [],
      },
      {
        type: 'line',
        name: PtsPatientArrivalKeyLabel.patientHandled,
        data: [],
      },
    ];
    for (const row of data) {
      let timestamp: number = new Date(row.dateHour).valueOf();
      let { physicianLoad, appLoad, patientHandled } = row;
      seriesData[0].data.push({
        x: timestamp,
        y: physicianLoad,
      });
      seriesData[1].data.push({
        x: timestamp,
        y: appLoad,
      });
      seriesData[2].data.push({
        x: timestamp,
        y: patientHandled,
      });
    }

    return { seriesData, shiftData };
  }
}
export default PtArrivalsPtsCoveredModel;

export const PTS_COVERED_GRAPH_OPTIONS: ApexOptions = {
  chart: {
    type: 'bar',
    height: 250,
    stacked: true,
    toolbar: {
      show: true,
      tools: {
        download: false,
        reset: false,
        zoomin: false,
        zoomout: false,
        pan: false,
        selection: false,
        zoom: false,
      },
    },
  },
  colors: ['#fff280', '#78c0f0', '#FF0000'],
  xaxis: {
    type: 'datetime',
    labels: {
      formatter: function (value) {
        return toDisplayDate(value, DATE_FORMAT.TIME_HR_ONLY);
      },
      rotateAlways: true,
    },
    title: {
      text: `${TEXT_CONTENT.HOUR}`,
    },
    tickAmount: 'dataPoints',
    tooltip: { enabled: false },
  },
  yaxis: {
    // min: 0,
    // max: 10,
    tickAmount: 3,
    decimalsInFloat: 0,
  },
  title: {
    text: TEXT_CONTENT.PT_ARRIVAL_VS_PTS_COVERED,
  },
  stroke: {
    show: true,
    curve: 'stepline',
    lineCap: 'butt',
    width: 2,
  },
  plotOptions: {
    bar: {
      columnWidth: '70%',
      barHeight: '100%',
      isDumbbell: true,
    },
  },
  markers: {
    size: 1.5,
    strokeWidth: 0,
    hover: {
      size: 5,
    },
  },
  tooltip: {
    intersect: true,
    shared: false,
    x: {
      formatter: function (value) {
        return toDisplayDate(value, DATE_FORMAT.DAY_WITH_TIME);
      },
    },
    y: {
      formatter: function (value) {
        return `${value.toFixed(2)}`;
      },
    },
  },
};
